import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Autocomplete,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Paper,
  InputAdornment
} from '@mui/material';
import {
  Search as SearchIcon,
  PersonAdd as AssignIcon
} from '@mui/icons-material';
import * as clinicalSupervisorApi from '../../../services/clinicalSupervisorApi';

function AssignClientsModal({ open, onClose, selectedClients, serviceGroups, token, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [therapists, setTherapists] = useState([]);
  const [selectedTherapist, setSelectedTherapist] = useState(null);
  const [therapistSearchQuery, setTherapistSearchQuery] = useState('');
  const [therapistSearchLoading, setTherapistSearchLoading] = useState(false);

  useEffect(() => {
    if (open) {
      loadTherapists();
      setError('');
      setSelectedTherapist(null);
    }
  }, [open, token]);

  const loadTherapists = async () => {
    try {
      setTherapistSearchLoading(true);
      // Get all therapists from supervisor's service groups
      const response = await clinicalSupervisorApi.getAssignedTherapists(true, token);
      setTherapists(response.therapists || []);
    } catch (err) {
      console.error('Error loading therapists:', err);
      setError('Failed to load therapists');
    } finally {
      setTherapistSearchLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedTherapist) {
      setError('Please select a therapist');
      return;
    }

    if (selectedClients.length === 0) {
      setError('No clients selected');
      return;
    }

    // Check capacity
    const currentCount = selectedTherapist.therapistInfo?.assignedClientCount || 0;
    const maxCapacity = selectedTherapist.therapistInfo?.maxClientCapacity || 20;
    const availableSlots = maxCapacity - currentCount;

    if (selectedClients.length > availableSlots) {
      setError(`Therapist only has ${availableSlots} available slots. You selected ${selectedClients.length} clients.`);
      return;
    }

    try {
      setLoading(true);
      setError('');

      await clinicalSupervisorApi.assignClientsToTherapist(
        selectedClients.map(c => c._id),
        selectedTherapist._id,
        token
      );

      onSuccess(`Successfully assigned ${selectedClients.length} client(s) to ${selectedTherapist.name}`);
      onClose();
    } catch (err) {
      console.error('Error assigning clients:', err);
      setError(err.response?.data?.msg || 'Failed to assign clients');
    } finally {
      setLoading(false);
    }
  };

  const filteredTherapists = therapistSearchQuery
    ? therapists.filter(t =>
        t.name?.toLowerCase().includes(therapistSearchQuery.toLowerCase()) ||
        t.email?.toLowerCase().includes(therapistSearchQuery.toLowerCase())
      )
    : therapists;

  const getClientServiceGroup = (client) => {
    const activeGroup = client.serviceGroups?.find(sg => sg.isActive && sg.roles?.includes('client'));
    return activeGroup?.groupId?.name || 'Unknown';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <AssignIcon />
          Assign Clients to Therapist
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Selected Clients Summary */}
        <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f5f5f5' }}>
          <Typography variant="subtitle2" gutterBottom>
            Selected Clients ({selectedClients.length}):
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
            {selectedClients.slice(0, 5).map(client => (
              <Chip
                key={client._id}
                label={`${client.name} (${getClientServiceGroup(client)})`}
                size="small"
                color="primary"
                variant="outlined"
              />
            ))}
            {selectedClients.length > 5 && (
              <Chip
                label={`+${selectedClients.length - 5} more`}
                size="small"
                color="primary"
              />
            )}
          </Box>
        </Paper>

        {/* Therapist Selection */}
        <Autocomplete
          value={selectedTherapist}
          onChange={(e, newValue) => setSelectedTherapist(newValue)}
          inputValue={therapistSearchQuery}
          onInputChange={(e, newValue) => setTherapistSearchQuery(newValue)}
          options={filteredTherapists}
          getOptionLabel={(option) =>
            `${option.name} - ${option.therapistInfo?.assignedClientCount || 0}/${option.therapistInfo?.maxClientCapacity || 20} clients`
          }
          loading={therapistSearchLoading}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Therapist"
              placeholder="Start typing to search..."
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <>
                    {therapistSearchLoading ? <CircularProgress size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option._id}>
              <Box sx={{ width: '100%' }}>
                <Typography variant="body1">{option.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.email} • {option.therapistInfo?.assignedClientCount || 0}/{option.therapistInfo?.maxClientCapacity || 20} clients •{' '}
                  {(option.therapistInfo?.maxClientCapacity || 20) - (option.therapistInfo?.assignedClientCount || 0)} slots available
                </Typography>
              </Box>
            </li>
          )}
          noOptionsText="No therapists found"
        />

        {/* Therapist Capacity Info */}
        {selectedTherapist && (
          <Paper sx={{ p: 2, mt: 2, backgroundColor: '#e3f2fd' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="subtitle2">Selected: {selectedTherapist.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedTherapist.email}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="body2">
                  Current: {selectedTherapist.therapistInfo?.assignedClientCount || 0}/{selectedTherapist.therapistInfo?.maxClientCapacity || 20}
                </Typography>
                <Typography variant="body2" color={
                  ((selectedTherapist.therapistInfo?.maxClientCapacity || 20) - (selectedTherapist.therapistInfo?.assignedClientCount || 0)) >= selectedClients.length
                    ? 'success.main'
                    : 'error.main'
                }>
                  After assignment: {(selectedTherapist.therapistInfo?.assignedClientCount || 0) + selectedClients.length}/{selectedTherapist.therapistInfo?.maxClientCapacity || 20}
                </Typography>
              </Box>
            </Box>
          </Paper>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleAssign}
          variant="contained"
          disabled={loading || !selectedTherapist}
          startIcon={loading ? <CircularProgress size={20} /> : <AssignIcon />}
        >
          {loading ? 'Assigning...' : `Assign ${selectedClients.length} Client${selectedClients.length !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AssignClientsModal;
